import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LockIcon, LogOutIcon, UsersIcon, TrashIcon, MailIcon, PhoneIcon, MapPinIcon, CalendarIcon, SearchIcon, DownloadIcon, ChevronDownIcon, ChevronUpIcon, ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
export interface NetworkSubmission {
  id: string;
  timestamp: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  reasons: string[];
  socialInstagram?: string;
  socialFacebook?: string;
  socialTiktok?: string;
  nonprofitName?: string;
  nonprofitDirector?: string;
  nonprofitWebsite?: string;
  faithCity?: string;
  faithState?: string;
  faithOrgName?: string;
  faithWebsite?: string;
  businessName?: string;
  businessContact?: string;
  businessPhone?: string;
  businessWebsite?: string;
  accomplishmentWebsite?: string;
  accomplishmentSocial?: string;
  accomplishmentLinkedin?: string;
  identifiesHonduran?: string;
}
const STORAGE_KEY = 'haa_network_submissions';
const ADMIN_USER = 'jaymiranda';
const ADMIN_PASS = 'Tahoe@911$';
export function getSubmissions(): NetworkSubmission[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}
export function saveSubmission(sub: NetworkSubmission) {
  const existing = getSubmissions();
  existing.unshift(sub);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
}
function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
function SubmissionCard({
  sub,
  onDelete
}: {
  sub: NetworkSubmission;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return <motion.div layout initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-132">
      <div className="p-5 cursor-pointer hover:bg-gray-50/50 transition-colors" onClick={() => setExpanded(!expanded)} data-id="element-133">
        <div className="flex items-start justify-between gap-3" data-id="element-134">
          <div className="flex-1 min-w-0" data-id="element-135">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-136">
              <h3 className="font-nunito font-bold text-haa-navy text-base" data-id="element-137">
                {sub.firstName} {sub.lastName}
              </h3>
              <span className="text-xs font-nunito text-gray-400 flex items-center gap-1" data-id="element-138">
                <CalendarIcon className="w-3 h-3" data-id="element-139" />
                {formatDate(sub.timestamp)}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap" data-id="element-140">
              <span className="text-sm font-nunito text-gray-500 flex items-center gap-1" data-id="element-141">
                <MailIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-142" />
                {sub.email}
              </span>
              {sub.phone && <span className="text-sm font-nunito text-gray-500 flex items-center gap-1" data-id="element-143">
                  <PhoneIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-144" />
                  {sub.phone}
                </span>}
              {(sub.city || sub.state) && <span className="text-sm font-nunito text-gray-500 flex items-center gap-1" data-id="element-145">
                  <MapPinIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-146" />
                  {[sub.city, sub.state].filter(Boolean).join(', ')}
                </span>}
            </div>
            {sub.reasons.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-147">
                {sub.reasons.map((r, i) => <span key={i} className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-haa-blue font-nunito text-[11px] font-semibold" data-id="element-148">
                    {r}
                  </span>)}
              </div>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-149">
            <button onClick={e => {
            e.stopPropagation();
            onDelete(sub.id);
          }} className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors" title="Delete" data-id="element-150">
              <TrashIcon className="w-3.5 h-3.5 text-red-500" data-id="element-151" />
            </button>
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center" data-id="element-152">
              {expanded ? <ChevronUpIcon className="w-4 h-4 text-gray-400" data-id="element-153" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-154" />}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-155">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-156">
            <div className="px-5 pb-5 pt-0 border-t border-gray-100" data-id="element-157">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-id="element-158">
                {sub.nonprofitName && <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100" data-id="element-159">
                    <p className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-160">
                      Nonprofit Details
                    </p>
                    <p className="font-nunito text-sm text-haa-navy" data-id="element-161">
                      <strong data-id="element-162">Name:</strong> {sub.nonprofitName}
                    </p>
                    {sub.nonprofitDirector && <p className="font-nunito text-sm text-haa-navy" data-id="element-163">
                        <strong data-id="element-164">Director:</strong> {sub.nonprofitDirector}
                      </p>}
                    {sub.nonprofitWebsite && <p className="font-nunito text-sm text-haa-blue break-all" data-id="element-165">
                        <strong data-id="element-166">Website:</strong> {sub.nonprofitWebsite}
                      </p>}
                  </div>}
                {sub.businessName && <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100" data-id="element-167">
                    <p className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-168">
                      Business Details
                    </p>
                    <p className="font-nunito text-sm text-haa-navy" data-id="element-169">
                      <strong data-id="element-170">Name:</strong> {sub.businessName}
                    </p>
                    {sub.businessWebsite && <p className="font-nunito text-sm text-haa-blue break-all" data-id="element-171">
                        <strong data-id="element-172">Website/Social:</strong> {sub.businessWebsite}
                      </p>}
                  </div>}
                {(sub.accomplishmentWebsite || sub.accomplishmentSocial || sub.accomplishmentLinkedin) && <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100" data-id="element-173">
                    <p className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-174">
                      Accomplishments
                    </p>
                    {sub.accomplishmentWebsite && <p className="font-nunito text-sm text-haa-blue break-all" data-id="element-175">
                        <strong data-id="element-176">Website:</strong> {sub.accomplishmentWebsite}
                      </p>}
                    {sub.accomplishmentSocial && <p className="font-nunito text-sm text-haa-navy" data-id="element-177">
                        <strong data-id="element-178">Social:</strong> {sub.accomplishmentSocial}
                      </p>}
                    {sub.accomplishmentLinkedin && <p className="font-nunito text-sm text-haa-blue break-all" data-id="element-179">
                        <strong data-id="element-180">LinkedIn:</strong> {sub.accomplishmentLinkedin}
                      </p>}
                  </div>}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function AdminDashboard({
  onBack
}: {
  onBack: () => void;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [submissions, setSubmissions] = useState<NetworkSubmission[]>([]);
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (isAuthenticated) {
      setSubmissions(getSubmissions());
    }
  }, [isAuthenticated]);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === ADMIN_USER && password === ADMIN_PASS) {
      setIsAuthenticated(true);
      setLoginError('');
    } else {
      setLoginError('Invalid username or password');
    }
  };
  const handleDelete = (id: string) => {
    const updated = submissions.filter(s => s.id !== id);
    setSubmissions(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };
  const handleExportCSV = () => {
    if (submissions.length === 0) return;
    const headers = ['Date', 'First Name', 'Last Name', 'Email', 'Phone', 'City', 'State', 'Reasons', 'Nonprofit Name', 'Nonprofit Director', 'Nonprofit Website', 'Business Name', 'Business Website', 'Accomplishment Website', 'Accomplishment Social', 'Accomplishment LinkedIn'];
    const rows = submissions.map(s => [formatDate(s.timestamp), s.firstName, s.lastName, s.email, s.phone, s.city, s.state, s.reasons.join('; '), s.nonprofitName || '', s.nonprofitDirector || '', s.nonprofitWebsite || '', s.businessName || '', s.businessWebsite || '', s.accomplishmentWebsite || '', s.accomplishmentSocial || '', s.accomplishmentLinkedin || '']);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `haa-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const filtered = submissions.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.firstName.toLowerCase().includes(q) || s.lastName.toLowerCase().includes(q) || s.email.toLowerCase().includes(q) || s.city.toLowerCase().includes(q) || s.state.toLowerCase().includes(q) || s.reasons.some(r => r.toLowerCase().includes(q));
  });
  // Login Screen
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-gradient-to-br from-haa-navy via-haa-blue to-haa-midblue flex items-center justify-center p-4" data-id="element-181">
        <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8" data-id="element-182">
          <div className="text-center mb-8" data-id="element-183">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-184">
              <LockIcon className="w-6 h-6 text-white" data-id="element-185" />
            </div>
            <h1 className="font-bebas text-3xl text-haa-navy tracking-wider" data-id="element-186">
              Admin Access
            </h1>
            <p className="font-nunito text-gray-500 text-sm mt-1" data-id="element-187">
              HAA Network Submissions
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" data-id="element-188">
            <div data-id="element-189">
              <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-190">
                Username
              </label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors" placeholder="Enter username" autoComplete="username" data-id="element-191" />
            </div>
            <div data-id="element-192">
              <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-193">
                Password
              </label>
              <div className="relative" data-id="element-194">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors" placeholder="Enter password" autoComplete="current-password" data-id="element-195" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-196">
                  {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-197" /> : <EyeIcon className="w-4 h-4" data-id="element-198" />}
                </button>
              </div>
            </div>

            <AnimatePresence data-id="element-199">
              {loginError && <motion.p initial={{
              opacity: 0,
              y: -5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} className="font-nunito text-red-500 text-sm text-center" data-id="element-200">
                  {loginError}
                </motion.p>}
            </AnimatePresence>

            <motion.button type="submit" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full bg-gradient-to-r from-haa-blue to-haa-midblue text-white font-nunito font-bold text-base py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all" data-id="element-201">
              Sign In
            </motion.button>
          </form>

          <button onClick={onBack} className="w-full mt-4 flex items-center justify-center gap-2 font-nunito text-sm text-gray-400 hover:text-haa-blue transition-colors" data-id="element-202">
            <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-203" />
            Back to site
          </button>
        </motion.div>
      </div>;
  }
  // Dashboard
  return <div className="min-h-screen bg-gray-50" data-id="element-204">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-205">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between" data-id="element-206">
          <div className="flex items-center gap-3" data-id="element-207">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow" data-id="element-208">
              <UsersIcon className="w-4 h-4 text-white" data-id="element-209" />
            </div>
            <div data-id="element-210">
              <h1 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight" data-id="element-211">
                Network Submissions
              </h1>
              <p className="font-nunito text-xs text-gray-400" data-id="element-212">
                {submissions.length} total submissions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-213">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={onBack} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 font-nunito text-xs font-semibold text-gray-600 transition-colors" data-id="element-214">
              <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-215" />
              Site
            </motion.button>
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => {
            setIsAuthenticated(false);
            setUsername('');
            setPassword('');
          }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 font-nunito text-xs font-semibold text-red-600 transition-colors" data-id="element-216">
              <LogOutIcon className="w-3.5 h-3.5" data-id="element-217" />
              Logout
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-id="element-218">
        {/* Search + Export */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6" data-id="element-219">
          <div className="relative flex-1" data-id="element-220">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-221" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, city, reason..." className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors bg-white" data-id="element-222" />
          </div>
          <motion.button whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.97
        }} onClick={handleExportCSV} disabled={submissions.length === 0} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-nunito text-sm font-bold transition-all ${submissions.length > 0 ? 'bg-haa-navy text-white hover:bg-haa-blue shadow' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-223">
            <DownloadIcon className="w-4 h-4" data-id="element-224" />
            Export CSV
          </motion.button>
        </div>

        {/* Submissions List */}
        {filtered.length === 0 ? <div className="text-center py-20" data-id="element-225">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4" data-id="element-226">
              <UsersIcon className="w-7 h-7 text-gray-300" data-id="element-227" />
            </div>
            <p className="font-nunito text-gray-400 text-base" data-id="element-228">
              {submissions.length === 0 ? 'No submissions yet. They will appear here when users submit the Join Our Network form.' : 'No results match your search.'}
            </p>
          </div> : <div className="space-y-3" data-id="element-229">
            {filtered.map(sub => <SubmissionCard key={sub.id} sub={sub} onDelete={handleDelete} data-id="element-230" />)}
          </div>}
      </div>
    </div>;
}